/**
 * HDFC Credit Card "Year End Statement & Summary" PDF parser.
 *
 * Handles Swiggy HDFC, Tata Neu, Millennia, and any other HDFC CC that emits
 * the annual FY statement (APRIL-YY to MARCH-YY). Structurally different from
 * the monthly PDF format:
 *
 *  - Date column:   "DD-MMM-YYYY"  (e.g. "28-Nov-2024")
 *  - Credit marker: explicit "DR" / "CR" column (not a lone "+")
 *  - Multiple card numbers in one table (primary + add-on; all treated as one account)
 *  - No NeuCoins / Rewards column
 */

import type { ParsedStatement, ParsedTransaction } from "@paisa/types"

// ── Column x-boundaries ───────────────────────────────────────────────────
// Derived from coordinate analysis of real Tata Neu and Swiggy HDFC yearly PDFs.
const X_DATE_MAX = 120 // Date: x ≤ 120 (all dates appear at x=79)
const X_DESC_MIN = 140 // Description: 140 ≤ x < 582
const X_DESC_MAX = 582
const X_AMOUNT_MIN = 582 // Amount: 582 ≤ x < 650 (data right-aligned at ~600-630)
const X_AMOUNT_MAX = 650
const X_DRCR_MIN = 650 // DR/CR: 650 ≤ x < 700 (data at x=667)
const X_CARD_MIN = 700 // Card number: x ≥ 700 (ignored for parsing)

// ── Token classifiers ─────────────────────────────────────────────────────
/** "28-Nov-2024" */
const DATE_CELL = /^\d{2}-[A-Za-z]{3}-\d{4}$/
/** Indian comma-separated decimal: "5,107.04", "1,17,287.00" */
const AMOUNT_CELL = /^[\d,]+\.\d{2}$/
/** Row y-grouping tolerance */
const ROW_TOLERANCE = 4

// ── Detection ────────────────────────────────────────────────────────────

export function isHdfcCCYearlyPdf(rawContent: string): boolean {
  return /Year End Statement\s*&\s*Summary/i.test(rawContent) && /HDFC/i.test(rawContent)
}

// ── Public API ────────────────────────────────────────────────────────────

export function parseHdfcCCYearlyItems(items: Coord[]): ParsedStatement {
  const metadata = extractMetadata(items)
  const transactions = parseTransactions(items)

  return {
    bank: "hdfc",
    accountType: "credit_card",
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Text extraction ───────────────────────────────────────────────────────

export interface Coord {
  page: number
  x: number
  y: number
  str: string
}

// ── Metadata extraction ───────────────────────────────────────────────────

interface StatementMetadata {
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(items: Coord[]): StatementMetadata {
  const fullText = items.map((i) => i.str).join(" ")

  // "000652926XXXXXX7934" → last 4 = "7934"
  const cardMatch = fullText.match(/\d{6,}X{6}(\d{4})/)
  const accountLast4 = cardMatch ? cardMatch[1] : null

  // "Account Summary for the period from APRIL-24 to MARCH-25"
  let periodStart: string | null = null
  let periodEnd: string | null = null
  const periodMatch = fullText.match(/from\s+([A-Z]+-\d{2})\s+to\s+([A-Z]+-\d{2})/i)
  if (periodMatch) {
    periodStart = parseMonthYear(periodMatch[1], "start")
    periodEnd = parseMonthYear(periodMatch[2], "end")
  }

  return { accountLast4, periodStart, periodEnd }
}

// ── Transaction parsing ───────────────────────────────────────────────────

function parseTransactions(items: Coord[]): ParsedTransaction[] {
  const rows = buildRows(items)
  const transactions: ParsedTransaction[] = []

  for (const row of rows) {
    const dateItem = row.find((c) => c.x <= X_DATE_MAX && DATE_CELL.test(c.str.trim()))
    if (!dateItem) continue

    const date = parseDdMmmYyyy(dateItem.str.trim())
    if (!date) continue

    const drcrItem = row.find(
      (c) => c.x >= X_DRCR_MIN && c.x < X_CARD_MIN && /^(DR|CR)$/.test(c.str.trim()),
    )
    if (!drcrItem) continue
    const isCredit = drcrItem.str.trim() === "CR"

    const amountItem = [...row]
      .filter((c) => c.x >= X_AMOUNT_MIN && c.x < X_AMOUNT_MAX && AMOUNT_CELL.test(c.str.trim()))
      .sort((a, b) => b.x - a.x)[0]
    if (!amountItem) continue

    const amount = parseAmount(amountItem.str)
    if (amount === 0) continue

    const description = row
      .filter((c) => c.x >= X_DESC_MIN && c.x < X_DESC_MAX && c !== dateItem)
      .sort((a, b) => a.x - b.x)
      .map((c) => c.str)
      .join(" ")
      .replace(/\s{2,}/g, " ")
      .trim()

    if (!description) continue

    transactions.push({
      date,
      description,
      amount,
      type: isCredit ? "credit" : "debit",
      referenceNumber: null,
      valueDate: null,
      closingBalance: null,
      paymentMethod: null,
      extractedName: description,
      upiId: null,
      sourceCategory: null,
    })
  }

  return transactions
}

function buildRows(items: Coord[]): Coord[][] {
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null

    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      const bucketY = parseInt(key.slice(pagePrefix.length), 10)
      if (Math.abs(item.y - bucketY) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }

    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.entries()]
    .sort((a, b) => {
      const [pa, ya] = a[0].split(":").map(Number)
      const [pb, yb] = b[0].split(":").map(Number)
      return pa !== pb ? pa - pb : yb - ya
    })
    .map(([, row]) => row.sort((a, b) => a.x - b.x))
}

// ── Utilities — exported for unit testing ─────────────────────────────────

const MONTH_3: Record<string, string> = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
}

const MONTH_FULL: Record<string, { mm: string; lastDay: (year: number) => number }> = {
  january: { mm: "01", lastDay: () => 31 },
  february: {
    mm: "02",
    lastDay: (y) => (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28),
  },
  march: { mm: "03", lastDay: () => 31 },
  april: { mm: "04", lastDay: () => 30 },
  may: { mm: "05", lastDay: () => 31 },
  june: { mm: "06", lastDay: () => 30 },
  july: { mm: "07", lastDay: () => 31 },
  august: { mm: "08", lastDay: () => 31 },
  september: { mm: "09", lastDay: () => 30 },
  october: { mm: "10", lastDay: () => 31 },
  november: { mm: "11", lastDay: () => 30 },
  december: { mm: "12", lastDay: () => 31 },
}

/** "28-Nov-2024" → "2024-11-28" */
export function parseDdMmmYyyy(raw: string): string | null {
  const match = raw.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/)
  if (!match) return null
  const mm = MONTH_3[match[2].toLowerCase()]
  if (!mm) return null
  return `${match[3]}-${mm}-${match[1]}`
}

/**
 * "APRIL-24" → "2024-04-01" (start) or "2024-04-30" (end).
 * The 2-digit year is always 2000+.
 */
export function parseMonthYear(raw: string, boundary: "start" | "end"): string | null {
  const match = raw.trim().match(/^([A-Z]+)-(\d{2})$/i)
  if (!match) return null
  const info = MONTH_FULL[match[1].toLowerCase()]
  if (!info) return null
  const year = 2000 + parseInt(match[2], 10)
  if (boundary === "start") return `${year}-${info.mm}-01`
  return `${year}-${info.mm}-${String(info.lastDay(year)).padStart(2, "0")}`
}

function parseAmount(raw: string): number {
  const cleaned = raw.replace(/,/g, "").trim()
  const num = parseFloat(cleaned)
  return Number.isNaN(num) ? 0 : Math.abs(num)
}
